/**
 * CONTRACT.md §2.7 — buttons, the identity providers, and the segmented control.
 *
 * Default, `primary` (ink), `ochre` — **the one** action the page is asking for —
 * `ghost`, and `danger`. §2.7 ends "There is no `.btn--jade`", and §3 maps it to
 * `.btn--primary`; the union is that line, plus one the owner asked for on
 * 17 September: "destructive items and submits get a danger colour token, currently
 * identical to Comment". It is **not** the jade argument in reverse — jade was
 * refused because good news is not a colour, and a button that cancels a task is not
 * news, it is a thing that cannot be undone. It takes the `--bad-*` family the
 * ledger already uses for a failed line, and it takes no fill: a red *word* in a
 * list of moves, not a red block on the page.
 *
 * `.btn--danger` is the destination for a move, never for a dismissal: the button
 * that walks away from a destructive dialog is a `ghost` and says what keeping it
 * means ("Keep the task").
 *
 * `href` renders an `<a>`, everything else a `<button type="button">`. The
 * mockups use `<a class="btn">` for navigation and `<button class="btn">` for a
 * control, and which element it is decides whether Enter and Space both work,
 * so it is a prop rather than a guess.
 *
 * `ProviderButton` and `Or` are session 23's, and they are in this file because
 * §2.7 is one family and one file. `ProviderButton` is deliberately **not** a fifth
 * variant — the reason is on the component.
 */

import type { ReactNode } from 'react'

import type { SocialProvider } from '@fareast/shared'

import { asChildElement, cx, type Common } from './common.js'
import { externalLinkProps } from './links.js'

export type ButtonVariant = 'default' | 'primary' | 'ochre' | 'ghost' | 'danger'

const VARIANT_CLASS: Record<ButtonVariant, string | false> = {
  default: false,
  primary: 'btn--primary',
  ochre: 'btn--ochre',
  ghost: 'btn--ghost',
  danger: 'btn--danger',
}

type ButtonBase = Common & {
  readonly variant?: ButtonVariant | undefined
  /** `.btn--sm`. */
  readonly size?: 'sm' | undefined
  readonly children: ReactNode
}

export type ButtonProps = ButtonBase & {
  /** Given, the button is an `<a>`. For an internal route, use `asChild` and a `Link`. */
  readonly href?: string | undefined
  /** Hands the classes to one element child — a router `Link`, usually. */
  readonly asChild?: boolean | undefined
  readonly onClick?: (() => void) | undefined
  readonly disabled?: boolean | undefined
  readonly type?: 'button' | 'submit' | undefined
}

export function Button({
  variant,
  size,
  href,
  asChild,
  onClick,
  disabled,
  type,
  children,
  ...common
}: ButtonProps) {
  const className =
    cx('btn', VARIANT_CLASS[variant ?? 'default'], size === 'sm' && 'btn--sm') ?? 'btn'

  if (asChild === true) return asChildElement(children, className, common)

  if (href !== undefined) {
    // `links.ts` decides, not the page: "Join Meet" is a `<Button href>` and it
    // used to replace the portal in the same tab.
    return (
      <a className={className} href={href} {...externalLinkProps(href)} {...common}>
        {children}
      </a>
    )
  }
  return (
    <button
      className={className}
      type={type ?? 'button'}
      disabled={disabled}
      onClick={onClick}
      {...common}
    >
      {children}
    </button>
  )
}

/* --------------------------------------------- §2.7, the identity providers */

/**
 * The mark and the words, per provider.
 *
 * **The hex is the one exemption to §1's "if a value is not a token it is not
 * allowed"** (CONTRACT.md §2.7). Google's four colours and Microsoft's four squares
 * are somebody else's trademark: a token would be a redraw, and a redraw is a
 * licensing problem rather than a design decision. The label is the provider's own
 * permitted wording rather than ours, which is the other half of the same argument.
 *
 * `fill` is a presentation attribute and not an inline `style`, so §4.1 is untouched
 * and `expectNoInlineStyle` has nothing to say about it.
 */
const PROVIDER_MARKS: Record<SocialProvider, { readonly label: string; readonly mark: ReactNode }> =
  {
    google: {
      label: 'Sign in with Google',
      mark: (
        <svg className="btn__mark" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
          <path
            fill="#EA4335"
            d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
          />
          <path
            fill="#4285F4"
            d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
          />
          <path
            fill="#FBBC05"
            d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.28-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.55 10.78l7.98-6.19z"
          />
          <path
            fill="#34A853"
            d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
          />
        </svg>
      ),
    },
    microsoft: {
      label: 'Sign in with Microsoft',
      mark: (
        <svg className="btn__mark" viewBox="0 0 23 23" aria-hidden="true" focusable="false">
          <path fill="#F25022" d="M1 1h10v10H1z" />
          <path fill="#7FBA00" d="M12 1h10v10H12z" />
          <path fill="#00A4EF" d="M1 12h10v10H1z" />
          <path fill="#FFB900" d="M12 12h10v10H12z" />
        </svg>
      ),
    },
  }

export type ProviderButtonProps = Common & {
  readonly provider: SocialProvider
  readonly onClick?: (() => void) | undefined
  readonly disabled?: boolean | undefined
}

/**
 * Sign in with Google, or with Microsoft. CONTRACT.md §2.7.
 *
 * A component rather than a fifth `ButtonVariant`, because the variant list is a
 * **rank of emphasis** — default, primary, ochre, ghost — and §2.7 closes it. A
 * `variant="provider"` would be claiming these out-rank `.btn--ghost`, which they do
 * not; they sit under the magic link, which is the door that works for everybody.
 *
 * **There is no `children` prop.** Both the label and the mark are derived from the
 * provider id, so there is nothing for a page to pass — which is what stops this
 * becoming an icon system (§5 refuses `.icon`), and what keeps Google's brand
 * guidelines enforceable in one place rather than at every call site.
 *
 * `common` is spread last, exactly as `Button` does, so `className` stays a type
 * error rather than a lint rule.
 */
export function ProviderButton({ provider, onClick, disabled, ...common }: ProviderButtonProps) {
  const { label, mark } = PROVIDER_MARKS[provider]

  return (
    <button
      className="btn btn--provider"
      type="button"
      disabled={disabled}
      onClick={onClick}
      {...common}
    >
      {mark}
      {label}
    </button>
  )
}

export type OrProps = Common

/**
 * The word between two ways in. CONTRACT.md §2.7.
 *
 * No `role="separator"`: it would announce a separator and swallow the word, and the
 * word is the content — the hairlines either side are `::before`/`::after` on this
 * one element rather than a rule with text laid over it.
 */
export function Or({ ...common }: OrProps) {
  return (
    <div className="or" {...common}>
      or
    </div>
  )
}

export type ButtonRowProps = Common & { readonly children: ReactNode }

export function ButtonRow({ children, ...common }: ButtonRowProps) {
  return (
    <div className="btn-row" {...common}>
      {children}
    </div>
  )
}

export type SegProps = Common & {
  /** What the group of choices is — a segmented control with no name is a puzzle. */
  readonly label: string
  readonly children: ReactNode
}

/**
 * The period selector. `role="group"` with a label, because visually the border
 * is what says "these three belong together" and a screen reader cannot see it.
 */
export function Seg({ label, children, ...common }: SegProps) {
  return (
    <div className="seg" role="group" aria-label={label} {...common}>
      {children}
    </div>
  )
}

export type SegItemProps = Common & {
  /** `.is-on` — the chosen one. Also `aria-pressed`. */
  readonly on?: boolean | undefined
  readonly onClick?: (() => void) | undefined
  readonly children: ReactNode
}

export function SegItem({ on, onClick, children, ...common }: SegItemProps) {
  return (
    <button
      className={on === true ? 'is-on' : undefined}
      type="button"
      aria-pressed={on === true}
      onClick={onClick}
      {...common}
    >
      {children}
    </button>
  )
}
